import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogContent, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { type ChangeEvent, type DragEvent, type FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { ThemedSvg } from "@/ui/ThemedSvg";
import type { HubSpotCredentialUploadResponse } from "@/utils/models";
import { useHubSpotCredentialUpload } from "./useHubSpotCredentialUpload";

type HubSpotUploadModalProps = {
	open: boolean;
	onClose: () => void;
};

export const HubSpotUploadModal: FC<HubSpotUploadModalProps> = ({ open, onClose }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { uploadHubSpotCredentials, isUploading, resetUpload } = useHubSpotCredentialUpload();
	const [selectedFile, setSelectedFile] = useState<File>();
	const [isDragging, setIsDragging] = useState(false);
	const [result, setResult] = useState<HubSpotCredentialUploadResponse>();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleDragOver = (e: DragEvent): void => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent): void => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleFileSelect = (e: ChangeEvent<HTMLInputElement>): void => {
		const file = e.target.files?.[0];
		if (file) {
			if (file.name.endsWith(".csv") || file.name.endsWith(".xls") || file.name.endsWith(".xlsx")) {
				setSelectedFile(file);
			} else {
				showSnackbar(t("hubspot.upload.invalid.file"), "error");
			}
		}
	};

	const handleDrop = (e: DragEvent): void => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];

		if (file) {
			if (file.name.endsWith(".csv") || file.name.endsWith(".xls") || file.name.endsWith(".xlsx")) {
				setSelectedFile(file);
			} else {
				showSnackbar(t("hubspot.upload.invalid.file"), "error");
			}
		}
	};

	const handleUpload = async (): Promise<void> => {
		if (!selectedFile || isUploading) return;
		const data = await uploadHubSpotCredentials(selectedFile);
		setResult(data);
	};

	const handleClose = () => {
		setSelectedFile(undefined);
		setResult(undefined);
		resetUpload();
		onClose();
	};

	const hasUnmatched = result && !result.success;
	const isSuccess = result?.success;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogContent sx={{ p: 4 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
					<Typography variant="h5">{t("hubspot.upload.title")}</Typography>
					<IconButton onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				</Box>

				{!result && (
					<>
						<Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
							{t("hubspot.upload.description")}
						</Typography>

						<Box
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							sx={{
								border: (theme) => `3px dashed ${theme.palette.primary.main}`,
								borderRadius: "12px",
								p: 6,
								textAlign: "center",
								cursor: "pointer",
								mb: 3,
								transition: "background-color 0.2s",
								backgroundColor: isDragging ? "#f5f5f5" : "transparent",
								"&:hover": { backgroundColor: "#f5f5f5" },
							}}
							onClick={() => fileInputRef.current?.click()}
						>
							<ThemedSvg src="/upload.svg" alt="Upload" size={64} color="var(--inhouse-primary)" sx={{ mb: 3 }} />
							<Typography variant="h6">{t("hubspot.upload.drag.drop")}</Typography>
							<Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
								{t("hubspot.upload.supported.formats")}
							</Typography>
							<input type="file" hidden ref={fileInputRef} accept=".csv,.xls,.xlsx" onChange={handleFileSelect} />
						</Box>

						{selectedFile && (
							<Box sx={{ mb: 3 }}>
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										<Typography variant="h6">{selectedFile.name}</Typography>
										<Typography variant="body1" color="textSecondary">
											{selectedFile.size < 1024
												? `${selectedFile.size} B`
												: selectedFile.size < 1024 * 1024
													? `${(selectedFile.size / 1024).toFixed(1)} KB`
													: `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
										</Typography>
									</Box>
									<IconButton
										size="medium"
										onClick={() => {
											setSelectedFile(undefined);
										}}
									>
										<CloseIcon sx={{ fontSize: 24 }} />
									</IconButton>
								</Box>
							</Box>
						)}

						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
							<Button
								variant="outlined"
								onClick={handleClose}
								size="large"
								sx={{ color: "primary.main", borderColor: "primary.main", "&:hover": { borderColor: "primary.dark" }, px: 4 }}
							>
								{t("cancel")}
							</Button>
							<Button
								variant="contained"
								disabled={!selectedFile || isUploading}
								onClick={handleUpload}
								size="large"
								sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, px: 4 }}
							>
								{isUploading ? t("hubspot.upload.uploading") : t("hubspot.upload.button")}
							</Button>
						</Box>
					</>
				)}

				{isSuccess && (
					<Box>
						<Box sx={{ textAlign: "center", py: 4 }}>
							<Typography variant="h6" sx={{ mb: 2 }}>
								{t("hubspot.upload.success")}
							</Typography>
							<Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
								{t("hubspot.upload.credentials.created", { count: result.credentials_created })}
							</Typography>
						</Box>

						{result.matched_staff.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" color="success" sx={{ mb: 1 }}>
									{t("hubspot.upload.matched.staff", { count: result.matched_staff.length })}
								</Typography>
								<TableContainer sx={{ maxHeight: 300 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>{t("first.name", { defaultValue: "First Name" })}</TableCell>
												<TableCell>{t("last.name", { defaultValue: "Last Name" })}</TableCell>
												<TableCell>{t("email", { defaultValue: "Email" })}</TableCell>
												<TableCell>{t("phone", { defaultValue: "Phone" })}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{result.matched_staff.map((staff) => (
												<TableRow key={staff.contact_id}>
													<TableCell>{staff.first_name || "-"}</TableCell>
													<TableCell>{staff.last_name || "-"}</TableCell>
													<TableCell>{staff.email || "-"}</TableCell>
													<TableCell>{staff.phone_number || "-"}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						{result.matched_locations.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" color="success" sx={{ mb: 1 }}>
									{t("hubspot.upload.matched.locations", { count: result.matched_locations.length })}
								</Typography>
								<TableContainer sx={{ maxHeight: 300 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>{t("hubspot.upload.company.name")}</TableCell>
												<TableCell>{t("hubspot.upload.staff.count")}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{result.matched_locations.map((loc) => (
												<TableRow key={loc.company_id || loc.company_name}>
													<TableCell>{loc.company_name}</TableCell>
													<TableCell>{loc.staff_count}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button variant="contained" onClick={handleClose} sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, px: 4 }}>
								{t("done")}
							</Button>
						</Box>
					</Box>
				)}

				{hasUnmatched && (
					<Box>
						<Typography variant="body1" color="error" sx={{ mb: 3 }}>
							{t("hubspot.upload.unmatched.message")}
						</Typography>

						{result.unmatched_staff.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" color="error" sx={{ mb: 1 }}>
									{t("hubspot.upload.unmatched.staff", { count: result.unmatched_staff.length })}
								</Typography>
								<TableContainer sx={{ maxHeight: 300 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>{t("first.name", { defaultValue: "First Name" })}</TableCell>
												<TableCell>{t("last.name", { defaultValue: "Last Name" })}</TableCell>
												<TableCell>{t("email", { defaultValue: "Email" })}</TableCell>
												<TableCell>{t("phone", { defaultValue: "Phone" })}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{result.unmatched_staff.map((staff) => (
												<TableRow key={staff.contact_id}>
													<TableCell>{staff.first_name || "-"}</TableCell>
													<TableCell>{staff.last_name || "-"}</TableCell>
													<TableCell>{staff.email || "-"}</TableCell>
													<TableCell>{staff.phone_number || "-"}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						{result.matched_staff.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" color="success" sx={{ mb: 1 }}>
									{t("hubspot.upload.matched.staff", { count: result.matched_staff.length })}
								</Typography>
								<TableContainer sx={{ maxHeight: 300 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>{t("first.name", { defaultValue: "First Name" })}</TableCell>
												<TableCell>{t("last.name", { defaultValue: "Last Name" })}</TableCell>
												<TableCell>{t("email", { defaultValue: "Email" })}</TableCell>
												<TableCell>{t("phone", { defaultValue: "Phone" })}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{result.matched_staff.map((staff) => (
												<TableRow key={staff.contact_id}>
													<TableCell>{staff.first_name || "-"}</TableCell>
													<TableCell>{staff.last_name || "-"}</TableCell>
													<TableCell>{staff.email || "-"}</TableCell>
													<TableCell>{staff.phone_number || "-"}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						{result.unmatched_locations.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" color="error" sx={{ mb: 1 }}>
									{t("hubspot.upload.unmatched.locations", { count: result.unmatched_locations.length })}
								</Typography>
								<TableContainer sx={{ maxHeight: 300 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>{t("hubspot.upload.company.name")}</TableCell>
												<TableCell>{t("hubspot.upload.staff.count")}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{result.unmatched_locations.map((loc) => (
												<TableRow key={loc.company_id || loc.company_name}>
													<TableCell>{loc.company_name}</TableCell>
													<TableCell>{loc.staff_count}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						{result.matched_locations.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" color="success" sx={{ mb: 1 }}>
									{t("hubspot.upload.matched.locations", { count: result.matched_locations.length })}
								</Typography>
								<TableContainer sx={{ maxHeight: 300 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>{t("hubspot.upload.company.name")}</TableCell>
												<TableCell>{t("hubspot.upload.staff.count")}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{result.matched_locations.map((loc) => (
												<TableRow key={loc.company_id || loc.company_name}>
													<TableCell>{loc.company_name}</TableCell>
													<TableCell>{loc.staff_count}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button variant="contained" onClick={handleClose} sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, px: 4 }}>
								{t("close")}
							</Button>
						</Box>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
